/**
 * @architecture-header 1
 * @layer module
 * @domain activities
 * @responsibility Render the answering screen with its working pad and escalation flow.
 * @state-owner student UI store
 * @allowed-imports React; student store; dataset and workspace stores; shared components and hooks
 * @forbidden teacher-only controls; direct API client
 * @public-api workspace main column
 * @side-effects schedules the simulated tutor reply and closes the run on submit
 * @tests workspace Playwright flows in frontend/apps/web/e2e/workspace.spec.ts
 */

import { useState } from 'react';
import { ModalDocument } from '../../../shared/components/Modal';
import { useOverlay } from '../../../shared/components/OverlayHost';
import { Page, PageBody, PageHeader } from '../../../shared/components/PageShell';
import { Toolbar } from '../../../shared/components/VTable';
import { PRACTICE_QUESTIONS, WRONG_HINTS } from '../../../shared/lib/demo-data';
import type { RunRecord } from '../../../shared/lib/model';
import { currentTimeLabel, runName } from '../../../shared/lib/selectors';
import { useDatasetStore } from '../../../shared/model/dataset-store';
import { useTranslator } from '../../../shared/model/use-workspace';
import { useWorkspaceUiStore } from '../../../shared/model/workspace-store';
import { useStudentUiStore } from '../store/student-ui-store';

export function StudentActivityPlayer({ run }: { run: RunRecord }) {
  const t = useTranslator();
  const { openModal, closeModal } = useOverlay();
  const classes = useDatasetStore((state) => state.classes);
  const activities = useDatasetStore((state) => state.activities);
  const notify = useWorkspaceUiStore((state) => state.notify);
  const navigate = useWorkspaceUiStore((state) => state.navigate);
  const question = useStudentUiStore((state) => state.question);
  const setQuestion = useStudentUiStore((state) => state.setQuestion);
  const answers = useStudentUiStore((state) => state.answers);
  const selectOption = useStudentUiStore((state) => state.selectOption);
  const threadOpen = useStudentUiStore((state) => state.threadOpen);
  const toggleThread = useStudentUiStore((state) => state.toggleThread);
  const [answer, setAnswer] = useState('');

  const total = PRACTICE_QUESTIONS.length;
  const current = PRACTICE_QUESTIONS[question] ?? PRACTICE_QUESTIONS[0];
  const picked = answers[question] ?? -1;
  const answeredCount = Object.keys(answers).length;

  const registerWrong = (said: string) => {
    const store = useStudentUiStore.getState();
    const tries = store.registerTry();
    store.openThread();
    store.pushMessage({ from: 'stu', text: said, at: currentTimeLabel() });
    store.setTyping(true);
    window.setTimeout(() => {
      const next = useStudentUiStore.getState();
      next.setTyping(false);
      next.pushMessage({
        from: 'ai',
        text: WRONG_HINTS[Math.min(tries - 1, 2)],
        at: currentTimeLabel(),
      });
      if (tries >= 3 && !next.escalated) {
        next.pushMessage({ from: 'sys', text: '我講了三種說法都沒接住，已經請王老師進來' });
        next.escalate();
        useDatasetStore.getState().setSeatStatus(run.classIndex, '王柏睿', 'need');
      }
    }, 1200);
  };

  const checkAnswer = () => {
    const value = answer.trim();
    if (!value) {
      notify(t('先填一個答案'));
      return;
    }
    if (value === '4' || value === 'x=4' || value === 'x = 4') {
      useStudentUiStore.getState().pushMessage({
        from: 'cel',
        text: '答對了',
        subtitle: '移項變號這一步你自己想通了',
      });
      useStudentUiStore.getState().openThread();
      return;
    }
    registerWrong(`我算出 x = ${value}`);
  };

  const submit = () => {
    useDatasetStore.getState().updateRun(run.id, { status: 'ended' });
    openModal(
      <ModalDocument subtitle={runName(activities, run)} title={t('交出去了')}>
        <div className="dgrid">
          <div className="dcard">
            <h6>{t('這次成績')}</h6>
            <b className="big">9 / 12</b>
            <span>{t('自動批改的部分')}</span>
          </div>
          <div className="dcard">
            <h6>{t('進度')}</h6>
            <div className="pgrow">
              <i className="done" />
              {t('已交出')}
            </div>
            <div className="pgrow">
              <i className="wait" />
              {t('老師還要看 2 題')}
            </div>
          </div>
          <div className="dcard">
            <h6>{t('接下來')}</h6>
            <button
              className="bt pri"
              style={{ width: '100%', marginBottom: 8 }}
              type="button"
              onClick={() => {
                closeModal();
                navigate('rep', run.id);
              }}
            >
              {t('看這次的回顧')}
            </button>
          </div>
        </div>
        <p className="dnote">AI 陪你想通的那幾題，老師那邊也看得到。</p>
      </ModalDocument>,
    );
  };

  return (
    <Page maxWidth={900}>
      <PageHeader
        actions={
          <button className={`bt ${threadOpen ? '' : 'pri'}`} type="button" onClick={toggleThread}>
            {threadOpen ? '關閉助教' : t('我需要老師')}
          </button>
        }
        meta={`${classes[run.classIndex].name} · ${t('第')} ${question + 1} / ${total} ${t('題')}${
          run.mode === 'live' ? ` · ${t('課堂進行中')}` : ` · ${t('今晚 23:59 截止')}`
        }`}
        title={runName(activities, run)}
      />
      <Toolbar
        left={
          <span className="vsumline">
            {t('已完成 ')}
            <b>{answeredCount}</b>
            {` / ${total} 題 `}
            <em>·</em>
            {' AI 提示過 '}
            <b>2</b>
            {' 次'}
          </span>
        }
      />
      <PageBody>
        {/* `.pbar` and `.qnav` ship in the stylesheet for exactly this jump panel. */}
        <div className="pbar">
          <span className="pt">{`${t('第')} ${question + 1} / ${total} ${t('題')}`}</span>
          <span className="mtr">
            <i style={{ width: `${(answeredCount / total) * 100}%` }} />
          </span>
          <div className="sp" />
          <nav aria-label={t('題目導覽')} className="qnav">
            {PRACTICE_QUESTIONS.map((item, index) => (
              <button
                aria-current={index === question ? 'true' : undefined}
                className={`${answers[index] != null ? 'done2' : ''} ${
                  index === question ? 'on' : ''
                }`}
                key={item.prompt}
                type="button"
                onClick={() => setQuestion(index)}
              >
                {index + 1}
              </button>
            ))}
          </nav>
        </div>
        <section className="qcard">
          <span className="qtag">{t('題目')}</span>
          <h2>{current.prompt}</h2>
          <div className="opts2">
            {current.options.map((option, index) => (
              <button
                className={`opt2 ${
                  picked === index ? (index === current.correct ? 'right' : 'pick') : ''
                }`}
                key={option}
                type="button"
                onClick={() => {
                  selectOption(question, index);
                  if (index !== current.correct) {
                    registerWrong(`${t('我選了 (')}${'ABCD'[index]})`);
                  }
                }}
              >
                <span className="kk">{'ABCD'[index]}</span>
                {option}
              </button>
            ))}
          </div>
          <div className="work">
            <div className="lb">{t('我的計算')}</div>
            <div className="wline">5x - 3 = 2x + 9</div>
            <div className="wline bad">
              5x + 2x = 9 + 3<em>{t('移項未變號')}</em>
            </div>
            <div className="ansrow">
              <label className="ai2">
                x =
                <input
                  id="ans"
                  onChange={(event) => setAnswer(event.target.value)}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter') checkAnswer();
                  }}
                  placeholder="?"
                  value={answer}
                />
              </label>
              <button className="bt pri" type="button" onClick={checkAnswer}>
                {t('檢查這一步')}
              </button>
            </div>
          </div>
          <div className="stepnav">
            <div className="sp" />
            <button
              className="bt sm"
              disabled={question === 0}
              type="button"
              onClick={() => setQuestion(Math.max(0, question - 1))}
            >
              {t('上一題')}
            </button>
            {question === total - 1 ? (
              <button className="bt sm pri" type="button" onClick={submit}>
                {t('交卷')}
              </button>
            ) : (
              <button className="bt sm pri" type="button" onClick={() => setQuestion(question + 1)}>
                {t('下一題')}
              </button>
            )}
          </div>
        </section>
      </PageBody>
    </Page>
  );
}
